import { DownloadOutlined, ExclamationCircleOutlined, UndoOutlined, ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons';
import { Alert, Button, Pagination, Space, Spin } from 'antd';
import React, { useEffect, useRef, useState, useImperativeHandle } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import * as XLSX from 'xlsx';
import { getMediaUrl } from '../../config';
import { downloadFile } from '../../utils/download';
import { 
  getUnifiedFileUrl, 
  getFileInfo, 
  getFileExtension, 
  getFileExtensionFromType,
  isPreviewable, 
  isZoomable as isFileZoomable,
  isHttpUrl 
} from '../../utils/fileUrlHelper';

// 动态导入docx-preview
let renderAsync = null;
let docxPreview = null;

// 异步加载docx-preview
const loadDocxPreview = async () => {
  try {
    if (!docxPreview) {
      docxPreview = await import('docx-preview');
      console.log('docx-preview module loaded:', docxPreview);
    }
    
    if (!renderAsync && docxPreview) {
      renderAsync = docxPreview.renderAsync;
      console.log('renderAsync extracted:', renderAsync);
    }
    
    if (renderAsync) {
      console.log('docx-preview loaded successfully, renderAsync type:', typeof renderAsync);
      return renderAsync;
    } else {
      console.error('renderAsync not found in docx-preview module');
      return null;
    }
  } catch (error) {
    console.error('Failed to load docx-preview:', error);
    return null;
  }
};

export const ZoomControls = ({ onZoomIn, onZoomOut, onReset, position = 'top' }) => {
  const buttonStyle = {
    position: position === 'top' ? 'absolute' : 'static',
    top: position === 'top' ? '0px' : 'auto',
    left: position === 'top' ? '50%' : 'auto',
    transform: position === 'top' ? 'translateX(-50%)' : 'none',
    zIndex: position === 'top' ? 10 : 'auto',
    backgroundColor: position === 'top' ? 'rgba(255, 255, 255, 0.98)' : 'transparent',
    border: position === 'top' ? '1px solid #e8e8e8' : 'none',
    borderRadius: position === 'top' ? '3px' : '0',
    boxShadow: position === 'top' ? '0 1px 3px rgba(0, 0, 0, 0.08)' : 'none',
    padding: position === 'top' ? '1px' : '0',
    // header位置的样式
    ...(position === 'header' && {
      position: 'static',
      backgroundColor: 'transparent',
      border: 'none',
      boxShadow: 'none',
      padding: '0'
    }),
    // 右下角浮动样式 - 使用fixed定位固定在视窗中
    ...(position === 'bottom-right' && {
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      top: 'auto',
      left: 'auto',
      transform: 'none',
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      border: '1px solid #d9d9d9',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      padding: '6px',
      zIndex: 1000,
      backdropFilter: 'blur(4px)'
    })
  };

  return (
    <Space style={buttonStyle}>
      <Button size="small" icon={<ZoomInOutlined />} onClick={onZoomIn}>放大</Button>
      <Button size="small" icon={<ZoomOutOutlined />} onClick={onZoomOut}>缩小</Button>
      <Button size="small" icon={<UndoOutlined />} onClick={onReset}>重置</Button>
    </Space>
  );
};

// 外部DOCX文件查看器组件
const ExternalDocxViewer = ({ fileUrl, document }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(1);
  const previewContainerRef = useRef(null);

  useEffect(() => {
    const loadDocxContent = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('开始加载外部DOCX文件:', fileUrl);
        
        // 尝试多种方式加载docx-preview
        let renderFunction = null;
        
        // 方法1: 使用现有的loadDocxPreview函数
        try {
          renderFunction = await loadDocxPreview();
          console.log('方法1结果 - renderFunction:', renderFunction);
        } catch (error) {
          console.error('方法1失败:', error);
        }
        
        // 方法2: 直接使用require（如果方法1失败）
        if (!renderFunction) {
          try {
            const docxPreview = require('docx-preview');
            renderFunction = docxPreview.renderAsync;
            console.log('方法2结果 - renderFunction:', renderFunction);
          } catch (error) {
            console.error('方法2失败:', error);
          }
        }
        
        // 方法3: 直接动态import（如果前两种方法都失败）
        if (!renderFunction) {
          try {
            const docxPreview = await import('docx-preview');
            renderFunction = docxPreview.renderAsync;
            console.log('方法3结果 - renderFunction:', renderFunction);
          } catch (error) {
            console.error('方法3失败:', error);
          }
        }
        
        if (!renderFunction) {
          throw new Error('DOCX预览组件不可用 - 所有加载方法都失败了');
        }
        
        const response = await fetch(fileUrl, { 
          headers: { 'Accept': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' } 
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const blob = await response.blob();
        console.log('DOCX文件blob大小:', blob.size);
        
        if (previewContainerRef.current && renderFunction) {
          console.log('开始渲染DOCX文件...');
          await renderFunction(blob, previewContainerRef.current);
          console.log('DOCX文件渲染完成');
        } else {
          throw new Error('DOCX预览组件不可用 - 容器或渲染函数不可用');
        }
      } catch (err) {
        console.error('Failed to load external DOCX:', err);
        setError('无法加载外部DOCX文件，请检查文件链接是否有效');
      } finally {
        setLoading(false);
      }
    };

    loadDocxContent();
  }, [fileUrl]);

  const handleZoomIn = () => setZoom(prev => prev + 0.1);
  const handleZoomOut = () => setZoom(prev => Math.max(0.1, prev - 0.1));
  const handleZoomReset = () => setZoom(1);

  const zoomStyle = { transform: `scale(${zoom})`, transformOrigin: 'top left' };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>正在加载DOCX文件...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <Alert
          message="DOCX文件加载失败"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Button icon={<DownloadOutlined />} onClick={() => downloadFile(document)}>下载文件</Button>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', overflow: 'auto', padding: '20px', position: 'relative' }}>
      <div ref={previewContainerRef} style={{ ...zoomStyle, width: '100%', minHeight: '60vh', overflow: 'visible' }} />
      <ZoomControls 
        onZoomIn={handleZoomIn} 
        onZoomOut={handleZoomOut} 
        onReset={handleZoomReset} 
        position="bottom-right"
      />
    </div>
  );
};

// 外部Markdown文件查看器组件
const ExternalMarkdownViewer = ({ fileUrl, document }) => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    const loadMarkdownContent = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch(fileUrl);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const text = await response.text();
        setContent(text);
      } catch (err) {
        console.error('Failed to load external markdown:', err);
        setError('无法加载外部Markdown文件，请检查文件链接是否有效');
      } finally {
        setLoading(false);
      }
    };

    loadMarkdownContent();
  }, [fileUrl]);

  const handleZoomIn = () => setZoom(prev => prev + 0.1);
  const handleZoomOut = () => setZoom(prev => Math.max(0.1, prev - 0.1));
  const handleZoomReset = () => setZoom(1);

  const zoomStyle = { fontSize: `${zoom * 100}%` };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>正在加载Markdown文件...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <Alert
          message="Markdown文件加载失败"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Button icon={<DownloadOutlined />} onClick={() => downloadFile(document)}>下载文件</Button>
      </div>
    );
  }

  return (
    <div style={{ height: '70vh', position: 'relative' }}>
      <div style={{ 
        height: '100%',
        overflow: 'auto', 
        padding: '20px',
        ...zoomStyle, 
        lineHeight: '1.6' 
      }}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            code: ({ node, inline, className, children, ...props }) => {
              const match = /language-(\w+)/.exec(className || '');
              return !inline && match ? (
                <pre style={{ backgroundColor: '#f6f8fa', padding: '16px', borderRadius: '6px', overflow: 'auto' }}>
                  <code className={className} {...props}>
                    {children}
                  </code>
                </pre>
              ) : (
                <code className={className} {...props} style={{ backgroundColor: '#f1f3f4', padding: '2px 4px', borderRadius: '3px' }}>
                  {children}
                </code>
              );
            },
            table: ({ children }) => (
              <table style={{ borderCollapse: 'collapse', width: '100%', marginBottom: '16px' }}>
                {children}
              </table>
            ),
            th: ({ children }) => (
              <th style={{ border: '1px solid #d0d7de', padding: '8px 12px', backgroundColor: '#f6f8fa', fontWeight: 'bold' }}>
                {children}
              </th>
            ),
            td: ({ children }) => (
              <td style={{ border: '1px solid #d0d7de', padding: '8px 12px' }}>
                {children}
              </td>
            ),
            h1: ({ children }) => <h1 style={{ fontSize: '2em', marginBottom: '16px', marginTop: '24px' }}>{children}</h1>,
            h2: ({ children }) => <h2 style={{ fontSize: '1.5em', marginBottom: '12px', marginTop: '20px' }}>{children}</h2>,
            h3: ({ children }) => <h3 style={{ fontSize: '1.25em', marginBottom: '8px', marginTop: '16px' }}>{children}</h3>,
            ul: ({ children }) => <ul style={{ marginBottom: '16px', paddingLeft: '20px' }}>{children}</ul>,
            ol: ({ children }) => <ol style={{ marginBottom: '16px', paddingLeft: '20px' }}>{children}</ol>,
            blockquote: ({ children }) => (
              <blockquote style={{ borderLeft: '4px solid #d0d7de', margin: '16px 0', paddingLeft: '16px', color: '#57606a' }}>
                {children}
              </blockquote>
            ),
            a: ({ href, children }) => (
              <a href={href} style={{ color: '#0969da', textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">
                {children}
              </a>
            ),
          }}
        >
          {content}
        </ReactMarkdown>
      </div>
      <ZoomControls 
        onZoomIn={handleZoomIn} 
        onZoomOut={handleZoomOut} 
        onReset={handleZoomReset} 
        position="bottom-right"
      />
    </div>
  );
};

// 这些工具函数已移动到 fileUrlHelper.js 中

const toGooglePreviewUrl = (url) => {
  if (!url) return url;
  try {
    const u = new URL(url);
    const host = u.host || '';
    const path = u.pathname || '';
    // Drive file -> /preview, keep query params like resourcekey
    if (/drive\.google\.com/i.test(host) && /^\/file\/d\//.test(path)) {
      if (/^\/file\/d\/[^/]+\/preview/i.test(path)) {
        return u.toString();
      }
      if (/^\/file\/d\/[^/]+\/(view|edit)(?:\/.*)?$/i.test(path)) {
        const newPath = path.replace(/\/file\/d\/([^/]+)\/(view|edit)(?:\/.*)?$/i, '/file/d/$1/preview');
        u.pathname = newPath;
        return u.toString();
      }
      return u.toString();
    }
    // Docs/Sheets/Slides -> /preview, keep query params
    if (/docs\.google\.com/i.test(host)) {
      if (/\/(document|spreadsheets|presentation)\/d\/[^/]+\/preview/i.test(path)) {
        return u.toString();
      }
      let newPath = path
        .replace(/\/document\/d\/([^/]+)\/(edit|view).*/i, '/document/d/$1/preview')
        .replace(/\/spreadsheets\/d\/([^/]+)\/(edit|view).*/i, '/spreadsheets/d/$1/preview')
        .replace(/\/presentation\/d\/([^/]+)\/(edit|view).*/i, '/presentation/d/$1/preview');
      if (newPath !== path) {
        u.pathname = newPath;
      }
      return u.toString();
    }
  } catch (e) {
    // ignore
  }
  return url;
};

const buildDriveGviewFallback = (url) => {
  try {
    const u = new URL(url);
    const path = u.pathname || '';
    const idMatch = path.match(/\/file\/d\/([^/]+)/i);
    const id = idMatch ? idMatch[1] : null;
    if (!id) return null;
    const params = new URLSearchParams(u.search || '');
    const rk = params.get('resourcekey');
    const uc = new URL('https://drive.google.com/uc');
    uc.searchParams.set('id', id);
    uc.searchParams.set('export', 'download');
    if (rk) uc.searchParams.set('resourcekey', rk);
    const gview = new URL('https://docs.google.com/gview');
    gview.searchParams.set('embedded', '1');
    gview.searchParams.set('url', uc.toString());
    return gview.toString();
  } catch (e) {
    return null;
  }
};

const buildDriveUcView = (url) => {
  try {
    const u = new URL(url);
    const path = u.pathname || '';
    const idMatch = path.match(/\/file\/d\/([^/]+)/i);
    const id = idMatch ? idMatch[1] : null;
    if (!id) return null;
    const params = new URLSearchParams(u.search || '');
    const rk = params.get('resourcekey');
    const uc = new URL('https://drive.google.com/uc');
    uc.searchParams.set('id', id);
    uc.searchParams.set('export', 'view');
    if (rk) uc.searchParams.set('resourcekey', rk);
    return uc.toString();
  } catch (e) {
    return null;
  }
};

const DocumentPreview = ({ document }) => {
  const previewContainerRef = useRef(null);
  const [excelHtml, setExcelHtml] = useState('');
  const [plainText, setPlainText] = useState('');
  const [zoom, setZoom] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fileSize, setFileSize] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [textChunks, setTextChunks] = useState([]);
  const [markdownContent, setMarkdownContent] = useState('');
  // 取消自动新开标签，改为内嵌预览 + 右上角按钮打开新标签

  // 文本文件分页配置
  const CHUNK_SIZE = 50000; // 每页50KB
  const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB限制 - 支持大文件预览

  const handleZoomIn = () => setZoom(prev => prev + 0.1);
  const handleZoomOut = () => setZoom(prev => Math.max(0.1, prev - 0.1));
  const handleZoomReset = () => setZoom(1);



  // 使用统一的文件URL获取方式
  const getFileUrl = () => {
    return getUnifiedFileUrl(document);
  };

  // 获取文件信息
  const getDocumentFileInfo = () => {
    return getFileInfo(document);
  };

  // 移除自动新开标签行为

  // 加载Markdown内容
  const loadMarkdownContent = async (fileUrl) => {
    try {
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error('Network response was not ok');
      const contentLength = response.headers.get('content-length');
      const fileSize = contentLength ? parseInt(contentLength) : 0;
      setFileSize(fileSize);
      if (fileSize > MAX_FILE_SIZE) {
        setError(`文件过大 (${(fileSize / 1024 / 1024).toFixed(1)}MB)，建议下载后查看`);
        return;
      }
      const content = await response.text();
      setMarkdownContent(content);
    } catch (error) {
      console.error("Markdown file loading failed:", error);
      setError('Markdown文件加载失败，请检查文件是否损坏');
    }
  };

  // 分页加载文本内容（仅本地文件）
  const loadTextChunk = async (fileUrl, page) => {
    try {
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error('Network response was not ok');

      const contentLength = response.headers.get('content-length');
      const fileSize = contentLength ? parseInt(contentLength) : 0;
      setFileSize(fileSize);

      // 检查文件大小
      if (fileSize > MAX_FILE_SIZE) {
        setError(`文件过大 (${(fileSize / 1024 / 1024).toFixed(1)}MB)，建议下载后查看`);
        return;
      }

      // 计算分页
      const totalPages = Math.ceil(fileSize / CHUNK_SIZE);
      setTotalPages(totalPages);

      // 获取当前页的内容
      const start = (page - 1) * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, fileSize);

      if (fileSize <= CHUNK_SIZE) {
        // 小文件直接加载全部
        const text = await response.text();
        setPlainText(text);
        setTextChunks([text]);
      } else {
        // 大文件分页加载
        const rangeResponse = await fetch(fileUrl, {
          headers: {
            'Range': `bytes=${start}-${end - 1}`
          }
        });

        if (rangeResponse.status === 206) {
          const chunk = await rangeResponse.text();
          setTextChunks(prev => {
            const newChunks = [...prev];
            newChunks[page - 1] = chunk;
            return newChunks;
          });
        } else {
          // 如果服务器不支持Range请求，回退到完整加载
          const text = await response.text();
          const chunks = [];
          for (let i = 0; i < text.length; i += CHUNK_SIZE) {
            chunks.push(text.slice(i, i + CHUNK_SIZE));
          }
          setTextChunks(chunks);
          setTotalPages(chunks.length);
        }
      }
    } catch (error) {
      console.error("Text file loading failed:", error);
      setError('文件加载失败，请检查文件是否损坏');
    }
  };

  useEffect(() => {
    const fileUrl = getFileUrl();
    if (!fileUrl) return;

    const fileInfo = getDocumentFileInfo();
    // 优先使用数据库字段获取扩展名
    const ext = getFileExtension(fileUrl, document);
    const external = fileInfo.isExternal;

    setLoading(true);
    setError(null);
    setMarkdownContent('');

    const processFile = async () => {
      try {
        if (ext === 'txt' && !external) {
          await loadTextChunk(fileUrl, currentPage);
        } else if (ext === 'md' && !external) {
          await loadMarkdownContent(fileUrl);
        } else if (ext === 'docx' && !external) {
          // 强制用 fetch+blob 渲染，避免服务端 Content-Disposition 导致浏览器下载
          console.log('开始加载本地DOCX文件:', fileUrl);
          const renderFunction = await loadDocxPreview();
          console.log('本地DOCX renderFunction:', renderFunction);
          if (!renderFunction) {
            throw new Error('DOCX预览组件不可用');
          }
          const response = await fetch(fileUrl, { headers: { 'Accept': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' } });
          if (!response.ok) throw new Error('Network response was not ok');
          const blob = await response.blob();
          console.log('本地DOCX blob大小:', blob.size);
          if (previewContainerRef.current && renderFunction) {
            console.log('开始渲染本地DOCX文件');
            await renderFunction(blob, previewContainerRef.current);
            console.log('本地DOCX文件渲染完成');
          }
        } else if (['xlsx', 'xls'].includes(ext) && !external) {
          const response = await fetch(fileUrl);
          if (!response.ok) throw new Error('Network response was not ok');
          const data = await response.arrayBuffer();
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const html = XLSX.utils.sheet_to_html(worksheet);
          setExcelHtml(html);
        } else {
          // 外链统一在新标签打开，此处不再处理
        }
      } catch (error) {
        console.error("File processing failed:", error);
        // 降级提示，允许用户下载
        setError('文件处理失败，可能不支持在线预览。您可以尝试下载后查看。');
      } finally {
        setLoading(false);
      }
    };

    processFile();
  }, [document, currentPage]);

  const fileUrl = getFileUrl();
  if (!fileUrl) {
    return <p>没有可用的文件进行预览。</p>;
  }

  const fileInfo = getDocumentFileInfo();
  const ext = getFileExtension(fileUrl, document);
  const external = fileInfo.isExternal;


  const isZoomable = isFileZoomable(ext);
  const isTextZoom = (ext === 'txt' || ext === 'md') && !external;

  const zoomStyle = isTextZoom
    ? { fontSize: `${zoom * 100}%` }
    : { transform: `scale(${zoom})`, transformOrigin: 'top left' };

  const renderPreview = () => {
    // 云文档（Google Docs/Drive）：使用iframe方案
    if (external && fileInfo.isGoogleDocs) {
      const openUrl = toGooglePreviewUrl(fileUrl);
      const openInNewTab = () => window.open(openUrl, '_blank', 'noopener');
      const header = (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <Button type="primary" onClick={openInNewTab}>在新标签打开</Button>
        </div>
      );
      return (
        <div>
          {header}
          <iframe src={openUrl} title={document?.title || 'cloud-doc'} style={{ width: '100%', height: '70vh', border: 'none' }} />
        </div>
      );
    }
    
    // 其他外链：根据文件类型尝试预览
    if (external) {
      const openInNewTab = () => window.open(fileUrl, '_blank', 'noopener');
      const header = (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <Button type="primary" onClick={openInNewTab}>在新标签打开</Button>
        </div>
      );
      
      // 根据文件扩展名决定预览方式
      const extOnly = getFileExtension(fileUrl, document);
      
      // 图片文件：直接显示
      if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg'].includes(extOnly)) {
        return (
          <div>
            {header}
            <img src={fileUrl} alt={document?.title || 'image'} style={{ maxWidth: '100%', maxHeight: '70vh' }} />
          </div>
        );
      }
      
      // PDF文件：使用iframe预览
      if (extOnly === 'pdf') {
        return (
          <div>
            {header}
            <iframe src={fileUrl} title={document?.title || 'pdf'} style={{ width: '100%', height: '70vh', border: 'none' }} />
          </div>
        );
      }
      
      // DOCX文件：使用fetch+blob渲染
      if (extOnly === 'docx') {
        return (
          <div style={{ height: '70vh', position: 'relative', overflow: 'auto' }}>
            {header}
            <ExternalDocxViewer fileUrl={fileUrl} document={document} />
          </div>
        );
      }
      
      // Markdown文件：使用fetch加载并用ReactMarkdown渲染
      if (['md', 'markdown'].includes(extOnly)) {
        return (
          <div>
            {header}
            <ExternalMarkdownViewer fileUrl={fileUrl} document={document} />
          </div>
        );
      }
      
      // 其他文本文件：尝试加载并显示
      if (['txt', 'json', 'xml', 'csv', 'html', 'htm'].includes(extOnly)) {
        return (
          <div>
            {header}
            <iframe src={fileUrl} title={document?.title || 'text'} style={{ width: '100%', height: '70vh', border: 'none' }} />
          </div>
        );
      }
      
      // 视频文件：使用video标签预览
      if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(extOnly)) {
        return (
          <div>
            {header}
            <video 
              controls 
              style={{ width: '100%', maxHeight: '70vh' }}
              preload="metadata"
            >
              <source src={fileUrl} type={`video/${extOnly}`} />
              您的浏览器不支持视频播放。
            </video>
          </div>
        );
      }
      
      // 其他文件：提供下载选项
      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <Alert
            message="外部文件预览"
            description="此文件类型暂不支持在线预览，建议下载后查看或在新标签页中打开。"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Space>
            <Button type="primary" onClick={openInNewTab}>在新标签打开</Button>
            <Button icon={<DownloadOutlined />} onClick={() => downloadFile(document)}>下载文件</Button>
          </Space>
        </div>
      );
    }

    switch (ext) {
      case 'docx':
        return (
          <div style={{ height: '70vh', position: 'relative', overflow: 'auto', padding: '20px' }}>
            {error && (
              <Alert
                message="DOCX 在线预览失败"
                description={
                  <div>
                    {error}
                    <div style={{ marginTop: 12 }}>
                      <Button icon={<DownloadOutlined />} onClick={() => downloadFile(document)}>下载文件</Button>
                    </div>
                  </div>
                }
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}
            <div ref={previewContainerRef} style={{ ...zoomStyle, width: '100%', minHeight: '60vh', overflow: 'visible' }} />
            <ZoomControls 
              onZoomIn={handleZoomIn} 
              onZoomOut={handleZoomOut} 
              onReset={handleZoomReset} 
              position="bottom-right"
            />
          </div>
        );
      case 'xlsx':
      case 'xls':
        return <div dangerouslySetInnerHTML={{ __html: excelHtml }} style={{ ...zoomStyle, width: '100%', height: '100%' }} />;
      case 'txt': {
        if (error) {
          return (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <Alert
                message="文件预览受限"
                description={error}
                type="warning"
                showIcon
              />
              <div style={{ marginTop: 16 }}>
                <Button icon={<DownloadOutlined />} onClick={() => downloadFile(document)}>下载文件</Button>
              </div>
            </div>
          );
        }
        return (
          <div style={{ height: '70vh', position: 'relative' }}>
            <div style={{ height: '100%', overflow: 'auto', padding: '20px' }}>
              <pre style={{ ...zoomStyle, whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>{plainText || (textChunks[currentPage - 1] || '')}</pre>
              {totalPages > 1 && (
                <Pagination
                  current={currentPage}
                  total={totalPages}
                  pageSize={1}
                  onChange={(page) => setCurrentPage(page)}
                  showSizeChanger={false}
                  style={{ marginTop: 16 }}
                />
              )}
            </div>
            <ZoomControls 
              onZoomIn={handleZoomIn} 
              onZoomOut={handleZoomOut} 
              onReset={handleZoomReset} 
              position="bottom-right"
            />
          </div>
        );
      }
      case 'md': {
        if (error) {
          return (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <Alert
                message="Markdown文件预览失败"
                description={error}
                type="warning"
                showIcon
              />
              <div style={{ marginTop: 16 }}>
                <Button icon={<DownloadOutlined />} onClick={() => downloadFile(document)}>下载文件</Button>
              </div>
            </div>
          );
        }
        return (
          <div style={{ height: '70vh', position: 'relative' }}>
            <div style={{ 
              height: '100%',
              overflow: 'auto', 
              padding: '20px',
              ...zoomStyle, 
              lineHeight: '1.6' 
            }}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  code: ({ node, inline, className, children, ...props }) => {
                    const match = /language-(\w+)/.exec(className || '');
                    return !inline && match ? (
                      <pre style={{ backgroundColor: '#f6f8fa', padding: '16px', borderRadius: '6px', overflow: 'auto' }}>
                        <code className={className} {...props}>
                          {children}
                        </code>
                      </pre>
                    ) : (
                      <code className={className} {...props} style={{ backgroundColor: '#f1f3f4', padding: '2px 4px', borderRadius: '3px' }}>
                        {children}
                      </code>
                    );
                  },
                  table: ({ children }) => (
                    <table style={{ borderCollapse: 'collapse', width: '100%', marginBottom: '16px' }}>
                      {children}
                    </table>
                  ),
                  th: ({ children }) => (
                    <th style={{ border: '1px solid #d0d7de', padding: '8px 12px', backgroundColor: '#f6f8fa', fontWeight: 'bold' }}>
                      {children}
                    </th>
                  ),
                  td: ({ children }) => (
                    <td style={{ border: '1px solid #d0d7de', padding: '8px 12px' }}>
                      {children}
                    </td>
                  ),
                  h1: ({ children }) => <h1 style={{ fontSize: '2em', marginBottom: '16px', marginTop: '24px' }}>{children}</h1>,
                  h2: ({ children }) => <h2 style={{ fontSize: '1.5em', marginBottom: '12px', marginTop: '20px' }}>{children}</h2>,
                  h3: ({ children }) => <h3 style={{ fontSize: '1.25em', marginBottom: '8px', marginTop: '16px' }}>{children}</h3>,
                  ul: ({ children }) => <ul style={{ marginBottom: '16px', paddingLeft: '20px' }}>{children}</ul>,
                  ol: ({ children }) => <ol style={{ marginBottom: '16px', paddingLeft: '20px' }}>{children}</ol>,
                  blockquote: ({ children }) => (
                    <blockquote style={{ borderLeft: '4px solid #d0d7de', margin: '16px 0', paddingLeft: '16px', color: '#57606a' }}>
                      {children}
                    </blockquote>
                  ),
                  a: ({ href, children }) => (
                    <a href={href} style={{ color: '#0969da', textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">
                      {children}
                    </a>
                  ),
                }}
              >
                {markdownContent}
              </ReactMarkdown>
            </div>
            <ZoomControls 
              onZoomIn={handleZoomIn} 
              onZoomOut={handleZoomOut} 
              onReset={handleZoomReset} 
              position="bottom-right"
            />
          </div>
        );
      }
      default:
        // 优先使用数据库的file_type_display字段进行判断
        if (document && document.file_type_display) {
          const fileType = document.file_type_display;
          
          // 根据数据库中的文件类型显示名称进行预览
          if (fileType === '图片文件') {
            return (
              <div style={{ textAlign: 'center' }}>
                <img
                  src={fileUrl}
                  alt={document?.title || 'image'}
                  style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                />
              </div>
            );
          }
          if (fileType === 'PDF文档') {
            return (
              <iframe src={fileUrl} title={document?.title || 'pdf'} style={{ width: '100%', height: '70vh', border: 'none' }} />
            );
          }
          if (fileType === '视频文件') {
            return (
              <video 
                controls 
                style={{ width: '100%', maxHeight: '70vh' }}
                preload="metadata"
              >
                <source src={fileUrl} type="video/mp4" />
                您的浏览器不支持视频播放。
              </video>
            );
          }
          if (fileType === '音频文件') {
            return (
              <audio 
                controls 
                style={{ width: '100%' }}
                preload="metadata"
              >
                <source src={fileUrl} type="audio/mpeg" />
                您的浏览器不支持音频播放。
              </audio>
            );
          }
        }
        
        // 如果数据库字段不可用，回退到扩展名判断
        // 其它本地类型（如图片、pdf）直接用浏览器渲染
        if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp'].includes(ext)) {
          return (
            <div style={{ textAlign: 'center' }}>
              <img
                src={fileUrl}
                alt={document?.title || 'image'}
                style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
              />
            </div>
          );
        }
        if (ext === 'pdf') {
          return (
            <iframe src={fileUrl} title={document?.title || 'pdf'} style={{ width: '100%', height: '70vh', border: 'none' }} />
          );
        }
        // 视频文件：使用video标签预览
        if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(ext)) {
          return (
            <video 
              controls 
              style={{ width: '100%', maxHeight: '70vh' }}
              preload="metadata"
            >
              <source src={fileUrl} type={`video/${ext}`} />
              您的浏览器不支持视频播放。
            </video>
          );
        }
        // 检查是否支持预览
        if (isPreviewable(ext)) {
          // 如果支持预览但没有特殊处理，尝试通用预览方式
          if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg'].includes(ext)) {
            return <img src={fileUrl} alt={document?.title || 'image'} style={{ maxWidth: '100%', maxHeight: '70vh' }} />;
          }
          if (ext === 'pdf') {
            return (
              <iframe src={fileUrl} title={document?.title || 'pdf'} style={{ width: '100%', height: '70vh', border: 'none' }} />
            );
          }
          if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(ext)) {
            return (
              <video 
                controls 
                style={{ width: '100%', maxHeight: '70vh' }}
                preload="metadata"
              >
                <source src={fileUrl} type={`video/${ext}`} />
                您的浏览器不支持视频播放。
              </video>
            );
          }
          // 其他支持预览的文件类型，使用iframe尝试预览
          return (
            <iframe 
              src={fileUrl} 
              title={document?.title || 'document'} 
              style={{ width: '100%', height: '70vh', border: 'none' }}
              onError={() => {
                console.warn('iframe预览失败，文件类型:', ext);
              }}
            />
          );
        }
        
        return <p>暂不支持该文件类型的内置预览，请下载后查看。</p>;
    }
  };

  return (
    <div>
      {renderPreview()}
    </div>
  );
};

export default DocumentPreview;
